"use client";

import { emitUsageEvent } from "../lib/umami";
import { useEffect, useState } from "react";
import type { NativeEntry } from "../lib/native-lookup";
import styles from "./ReferenceExplorers.module.css";

interface NativeResult {
  items: NativeEntry[];
  total: number;
  indexed: number;
  page: number;
  limit: number;
  namespaces: string[];
  source: string;
  sourceHealth?: { gta5: boolean; cfx: boolean };
}

function nativeUrl(entry: NativeEntry): string {
  return "https://docs.fivem.net/natives/?_" + entry.hash + "=";
}

export default function NativeFinder() {
  const [query, setQuery] = useState("");
  const [namespace, setNamespace] = useState("");
  const [apiSet, setApiSet] = useState("");
  const [browseAll, setBrowseAll] = useState(false);
  const [page, setPage] = useState(0);
  const [result, setResult] = useState<NativeResult | null>(null);
  const [selectedHash, setSelectedHash] = useState("");
  const [retry, setRetry] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copyStatus, setCopyStatus] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    let disposed = false;
    let requestTimeout: ReturnType<typeof setTimeout> | undefined;
    const debounceTimeout = setTimeout(async () => {
      setLoading(true);
      setError("");
      // The upstream API may stall during a source refresh. Never leave the UI loading forever.
      requestTimeout = setTimeout(() => controller.abort(), 20_000);
      try {
        const params = new URLSearchParams({
          q: query,
          namespace,
          apiset: apiSet,
          page: String(page),
          all: browseAll ? "1" : "0",
        });
        const response = await fetch("/api/native-lookup?" + params, { signal: controller.signal });
        if (!response.ok) throw new Error("Native index unavailable");
        const data: NativeResult = await response.json();
        if (disposed) return;
        setResult(data);
      } catch {
        if (!disposed) setError("Unable to load the native index. Retry or open the official reference.");
      } finally {
        clearTimeout(requestTimeout);
        if (!disposed) setLoading(false);
      }
    }, 250);
    return () => {
      disposed = true;
      clearTimeout(debounceTimeout);
      clearTimeout(requestTimeout);
      controller.abort();
    };
  }, [query, namespace, apiSet, browseAll, page, retry]);

  const selected = result?.items.find((item) => item.source + ":" + item.namespace + ":" + item.hash === selectedHash)
    ?? result?.items[0];
  const selectedKey = selected ? selected.source + ":" + selected.namespace + ":" + selected.hash : "";
  const pageCount = Math.max(1, Math.ceil((result?.total ?? 0) / (result?.limit ?? 24)));

  async function copy(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      emitUsageEvent("example_copy");
      setCopyStatus("Copied " + value);
    } catch {
      setCopyStatus("Clipboard unavailable; select the text to copy it.");
    }
  }

  return <section className={styles.explorer} aria-label="FiveM native finder">
    <div className={styles.heading}>
      <div>
        <h2>Find a FiveM native</h2>
        <p>Search by native name, old alias, CamelCase, namespace or hash. Inspect original types and follow links for language-specific signatures.</p>
      </div>
      <a href="https://docs.fivem.net/natives/" target="_blank" rel="noopener noreferrer">Full official reference ↗</a>
    </div>
    <div className={styles.filters}>
      <label className={styles.search}>
        <span>Search natives</span>
        <input type="search" placeholder="e.g. GetEntityCoords, old alias, CFX, 0x..."
          value={query} onChange={(event) => { setQuery(event.target.value); setPage(0); setSelectedHash(""); }} />
      </label>
      <label>
        <span>Namespace</span>
        <select value={namespace} onChange={(event) => {
          setNamespace(event.target.value); setBrowseAll(true); setPage(0); setSelectedHash("");
        }}>
          <option value="">All namespaces</option>
          {result?.namespaces.map((name) => <option key={name} value={name}>{name}</option>)}
        </select>
      </label>
      <label>
        <span>Verified Cfx API set</span>
        <select value={apiSet} onChange={(event) => {
          setApiSet(event.target.value); setBrowseAll(true); setPage(0); setSelectedHash("");
        }}>
          <option value="">All / unspecified</option>
          <option value="client">Client (Cfx declarations)</option>
          <option value="server">Server (Cfx declarations)</option>
        </select>
      </label>
      <label className={styles.checkbox}>
        <input type="checkbox" checked={browseAll} onChange={(event) => {
          setBrowseAll(event.target.checked); setPage(0); setSelectedHash("");
        }} />
        Browse all natives
      </label>
    </div>
    <p className={styles.muted} role="status" aria-live="polite">
      {error || (loading ? "Searching official native data…" : result
        ? result.total + " matching entries · " + result.indexed + " entries indexed from available official GTA V and Cfx sources"
        : "Loading official native data…")}
    </p>
    {error && <div className={styles.actions}>
      <button type="button" onClick={() => setRetry((count) => count + 1)}>
        Retry official data
      </button>
    </div>}
    {result?.sourceHealth && (!result.sourceHealth.gta5 || !result.sourceHealth.cfx) &&
      <p role="alert" className={styles.muted}>
        Some official native data is temporarily unavailable:
        {!result.sourceHealth.gta5 ? " GTA V" : ""}
        {!result.sourceHealth.cfx ? " Cfx" : ""}.
        Search results are incomplete until those sources recover.
      </p>}
    {apiSet && <p className={styles.muted}>API-set filtering uses only explicit Cfx declarations.
      GTA V natives lack source-defined client/server tags, so they are omitted from filtered results.</p>}
    {result && !error && <div className={styles.nativeLayout}>
      <div className={styles.nativeList} role="group" aria-label="Native search results">
        {result.items.map((entry) => <button type="button"
          className={styles.nativeItem} data-active={selectedKey === entry.source + ":" + entry.namespace + ":" + entry.hash}
          aria-pressed={selectedKey === entry.source + ":" + entry.namespace + ":" + entry.hash}
          key={entry.source + entry.namespace + entry.hash}
          onClick={() => setSelectedHash(entry.source + ":" + entry.namespace + ":" + entry.hash)}>
          <strong>{entry.name}</strong>
          <span>{entry.namespace} · {entry.hash}</span>
        </button>)}
        {result.items.length === 0 && <p className={styles.empty}>No matches. Try a shorter name, namespace or hash.</p>}
      </div>
      {selected && <div className={styles.nativeDetail} aria-label="Selected native details">
        <div className={styles.detailHeading}>
          <div><span className={styles.eyebrow}>{selected.namespace} native</span><h3>{selected.name}</h3></div>
          <a href={nativeUrl(selected)} target="_blank" rel="noopener noreferrer">Official definition ↗</a>
        </div>
        {selected.apiSets?.length ? <p className={styles.muted}>
          Verified API set: <strong>{selected.apiSets.join(", ")}</strong>
        </p> : <p className={styles.muted}>
          API set: not declared in this source. Check the official native page.
        </p>}
        {selected.description && <p className={styles.muted}>
          {selected.description}
          {selected.descriptionTruncated && <>… <a href={nativeUrl(selected)} target="_blank" rel="noopener noreferrer">Full official notes</a></>}
        </p>}
        {selected.aliases?.length ? <p className={styles.muted}>
          Also known as: {selected.aliases.join(", ")}
        </p> : null}
        <p className={styles.muted}>Hash: <code>{selected.hash}</code>
          {selected.jhash ? <> · Legacy hash: <code>{selected.jhash}</code></> : null}
        </p>
        <pre className={styles.code}><code>{selected.returns + " " + selected.name + "(" +
          (selected.params.length ? selected.params.map((arg) => arg.type + " " + arg.name).join(", ") : "void") + ");"}</code></pre>
        <div className={styles.actions}>
          <button type="button" onClick={() => copy(selected.name)}>Copy name</button>
          <button type="button" onClick={() => copy(selected.hash)}>Copy hash</button>
        </div>
        {selected.params.length > 0 && <div className={styles.paramTable}>
          <strong>Parameters</strong>
          <ul>{selected.params.map((arg, index) => <li key={index}>
            <code>{arg.name}</code> <span>{arg.type}</span>
            {arg.description && <small>{arg.description}</small>}
          </li>)}</ul>
          {selected.returnsDescription && <p className={styles.muted}>Returns: {selected.returnsDescription}</p>}
        </div>}
        <p className={styles.muted}>This is the raw native signature. Check the official page for
          client/server API set, Lua/JavaScript/C# binding, notes and examples before using it.</p>
      </div>}
    </div>}
    {result && !error && pageCount > 1 && <nav className={styles.pager} aria-label="Native result pages">
      <button type="button" disabled={page === 0} onClick={() => { setPage(page - 1); setSelectedHash(""); }}>Previous</button>
      <span>Page {page + 1} of {pageCount}</span>
      <button type="button" disabled={page >= pageCount - 1} onClick={() => { setPage(page + 1); setSelectedHash(""); }}>Next</button>
    </nav>}
    <p className={styles.muted} role="status">{copyStatus}</p>
    <p className={styles.sourceNote}>
      Sources: <a href="https://static.cfx.re/natives/natives.json">official GTA V native dump</a> and
      {" "}<a href="https://runtime.fivem.net/doc/natives_cfx.json">official Cfx native declarations</a>.
      Both indexes are refreshed by the server, not downloaded in full to your browser.
      Cfx API sets come from their official declarations; GTA V entries must be checked individually.
      RedM natives are not included. Consult <a href="https://docs.fivem.net/natives/">Cfx.re</a> for
      binding-specific signatures and runtime compatibility.
    </p>
  </section>;
}
